import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogConfig} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '@aviary/dialogs/confirmation-dialog/confirmation-dialog.component';
import {ConfirmationDialog, ConfirmationDialogDefinition} from '@aviary/dialogs/confirmation-dialog/confirmation-dialog.types';
import {CatalogSearchFilterOptionChangedEvent, CatalogSearchFilterSelectOptions} from '@store/catalog/catalog.types';
import {CatalogFilterOptionsComponent} from '@app/catalog/search/catalog-filters/catalog-filter-options/catalog-filter-options.component';
import {addSelectionStatusToFilterOptions, sortFilterOptionsWithSelectedFirst} from '@store/catalog/catalog.helpers';
import {EXTRA_LARGE_DIALOG_CONFIG} from '@aviary/dialogs/base-dialog/base-dialog.types';

export interface AllCatalogFiltersDialogData {
  filterDisplayName: string;
  options: CatalogSearchFilterSelectOptions;
}

const NUMBER_OF_GROUPS: number = 3;

@Component({
  selector: 'px-all-catalog-filters',
  template: `
    <mx-confirmation-dialog [definition]="dialogDefinition">
      <div class="grid grid-cols-3 gap-x-4">
        @for (optionGroup of optionGroups; track $index) {
          <px-catalog-filter-options [options]="optionGroup" (selectionChanged)="onSelectionChanged($event)" class="ms-5"/>
        }
      </div>
    </mx-confirmation-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ConfirmationDialogComponent, CatalogFilterOptionsComponent]
})
export class AllCatalogFiltersDialog implements ConfirmationDialog<AllCatalogFiltersDialogData, string[]> {
  public static readonly dialogConfigOverrides: MatDialogConfig = EXTRA_LARGE_DIALOG_CONFIG;

  public readonly dialogData: AllCatalogFiltersDialogData = inject(MAT_DIALOG_DATA);
  public optionGroups: CatalogSearchFilterSelectOptions[] = [];

  private readonly allOptions: CatalogSearchFilterSelectOptions = this.dialogData.options;

  private readonly selectedValues: Set<string> = new Set(
    this.allOptions
      .filter(option => option.selected)
      .map(option => option.value)
  );

  public readonly dialogDefinition: ConfirmationDialogDefinition<string[]> = {
    title: `Filter by ${this.dialogData.filterDisplayName}`,
    confirmButtonLabel: 'Apply',
    valueCalculator: () => [...this.selectedValues]
  };

  constructor() {
    this.updateOptionGroups();
  }

  public onSelectionChanged(event: CatalogSearchFilterOptionChangedEvent): void {
    if (event.selected) {
      this.selectedValues.add(event.value);
    } else {
      this.selectedValues.delete(event.value);
    }

    this.updateOptionGroups();
  }

  private updateOptionGroups(): void {
    this.optionGroups = this.splitIntoGroups(
      sortFilterOptionsWithSelectedFirst(
        addSelectionStatusToFilterOptions(this.allOptions, this.selectedValues)
      )
    );
  }

  private splitIntoGroups(options: CatalogSearchFilterSelectOptions): CatalogSearchFilterSelectOptions[] {
    const optionsPerGroup: number = Math.ceil(options.length / NUMBER_OF_GROUPS);
    const optionGroups: CatalogSearchFilterSelectOptions[] = Array.from({length: NUMBER_OF_GROUPS}, () => []);

    options.forEach((option, index) => {
      const groupIndex: number = Math.floor(index / optionsPerGroup);

      optionGroups[groupIndex].push(option);
    });

    return optionGroups;
  }
}
